// pages/display/SiteMap.js —— 站点地图：聚合指标 + SVG 中国地图底图 + 站点点位 + 浮窗详情 + 进入监控 + 全屏
// doc01 2.4：正常站点绿点、告警/通信异常红点；点击站点右侧浮窗展示基本信息并可「进入监控」；
//            顶部聚合指标（隐藏站点不计入），支持按省份 / 区县筛选、隐藏站点与全屏查看。
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Select, Input, Button, Tag, Tooltip } from 'antd';
import {
  EnvironmentOutlined, CompassOutlined, SearchOutlined, ArrowRightOutlined,
  AlertOutlined, FullscreenOutlined, FullscreenExitOutlined,
  EyeOutlined, EyeInvisibleOutlined, ReloadOutlined,
} from '@ant-design/icons';
import PageHead from '../../components/PageHead';
import {
  STATIONS, MAP_POINTS, STATION_REGIONS, getOverview, getMapMetrics,
} from '../../config/stationData';
import ChinaSiteMap from '../../components/ChinaSiteMap';

// 文档 2.4：地图站点仅两态 —— 正常（绿）/ 发生告警（红）
const STATE_TEXT = (st) => (st === 'online' ? '正常' : '告警');
const STATE_COLOR = (st) => (st === 'online' ? '#52e8b0' : '#ff5a7f');

// 顶部聚合指标（隐藏站点不计入）
const METRICS = getMapMetrics();

// 省份 / 区县筛选选项
const PROVINCE_OPTIONS = [
  { value: 'all', label: '全部省份' },
  ...[...new Set(Object.values(STATION_REGIONS).map((r) => r.province))].map((p) => ({ value: p, label: p })),
];

const REGION_OPTIONS = [
  { value: 'all', label: '全部区县' },
  ...Array.from(new Set(Object.values(STATION_REGIONS).map((r) => `${r.province}|${r.city}|${r.district}`)))
    .map((k) => {
      const [p, c, d] = k.split('|');
      return { value: d, label: `${p.slice(0, 2)} · ${c} · ${d}` };
    }),
];

const sumRated = (list, sel) => list.reduce((a, m) => a + (parseFloat(sel(m)) || 0), 0);

const SiteMap = () => {
  const navigate = useNavigate();
  const [selectedId, setSelectedId] = useState('hn');
  const [province, setProvince] = useState('all');
  const [region, setRegion] = useState('all');
  const [keyword, setKeyword] = useState('');
  const [hidden, setHidden] = useState([]);
  const [full, setFull] = useState(false);

  const markers = STATIONS.map((s) => {
    const pt = MAP_POINTS.find((p) => p.name === s.name);
    const regionInfo = STATION_REGIONS[s.id] || {};
    return {
      ...s,
      state: pt ? pt.state : 'online',
      lng: pt ? pt.lng : '—',
      lat: pt ? pt.lat : '—',
      province: regionInfo.province,
      district: regionInfo.district,
      hidden: hidden.includes(s.id),
      ov: getOverview(s.id),
    };
  });

  const selected = markers.find((m) => m.id === selectedId) || markers[0];
  const visible = markers.filter((m) => {
    if (m.hidden) return false;
    if (province !== 'all' && m.province !== province) return false;
    if (region !== 'all' && m.district !== region) return false;
    if (keyword && !m.name.includes(keyword)) return false;
    return true;
  });

  const counted = markers.filter((m) => !m.hidden);

  const toggleHide = (id) => {
    setHidden((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  // 「恢复全部显示」：清空省份 / 区县 / 搜索条件，并取消所有隐藏
  const resetAll = () => {
    setProvince('all');
    setRegion('all');
    setKeyword('');
    setHidden([]);
  };

  // 进入监控时携带当前站点，保证跳转后的单站与所选站点一致
  const enterMonitor = () => {
    navigate(`/app/monitor/station?station=${selectedId}`);
  };

  const KPI_ITEMS = [
    { label: '电站个数', value: String(counted.length), unit: '个', foot: hidden.length ? `已隐藏 ${hidden.length} 个站点，不计入统计` : '隐藏站点不计入统计' },
    { label: '总装机功率', value: sumRated(counted, (m) => m.rated.power).toFixed(2), unit: 'MW' },
    { label: '总装机容量', value: sumRated(counted, (m) => m.rated.capacity).toFixed(1), unit: 'MWh' },
    { label: '总充电量', value: METRICS.chargeEnergy, foot: '不含今日' },
    { label: '总放电量', value: METRICS.dischargeEnergy, foot: '不含今日' },
    { label: '多站累计收益', value: METRICS.totalIncome, foot: '不含今日' },
    { label: '昨日收益', value: METRICS.incomeYesterday },
  ];

  return (
    <div>
      <PageHead
        title="站点地图"
        desc="电站地理位置分布与运行态势总览（官方标准底图，支持中国 / 世界地图切换）"
        extra={(
          <Button
            icon={full ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
            onClick={() => setFull((v) => !v)}
          >
            {full ? '退出全屏' : '全屏地图'}
          </Button>
        )}
      />

      <div className="kpi-grid sitemap-kpi">
        {KPI_ITEMS.map((k) => (
          <div className="kpi-card" key={k.label}>
            <div className="kpi-label">{k.label}</div>
            <div className="kpi-value">{k.value}{k.unit ? <span className="unit">{k.unit}</span> : null}</div>
            {k.foot ? <div className="kpi-foot">{k.foot}</div> : null}
          </div>
        ))}
      </div>

      <div className="sm-bar">
        <span className="sm-bar-label">省份</span>
        <Select
          value={province}
          onChange={setProvince}
          style={{ width: 140 }}
          options={PROVINCE_OPTIONS}
        />
        <Select
          value={region}
          onChange={setRegion}
          style={{ width: 200 }}
          options={REGION_OPTIONS}
        />
        <Input
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          prefix={<SearchOutlined />}
          placeholder="搜索站点名称并定位"
          style={{ width: 220 }}
          allowClear
        />
        <Button icon={<ReloadOutlined />} onClick={resetAll}>恢复全部显示</Button>
        <span className="sm-bar-meta">
          显示 {visible.length} / 共 {markers.length} 个电站 · 当前 {STATE_TEXT(selected.state)}
        </span>
      </div>

      <div className={`sitemap-layout${full ? ' fullscreen' : ''}`}>
        {full ? (
          <Button
            className="sitemap-exit"
            icon={<FullscreenExitOutlined />}
            onClick={() => setFull(false)}
          >
            退出全屏
          </Button>
        ) : null}

        {/* 左侧：官方标准底图（中国 / 世界） */}
        <div className="sitemap-canvas">
          <ChinaSiteMap
            sites={visible}
            selectedId={selectedId}
            onSelect={(id) => { if (id) { setSelectedId(id); setKeyword(''); } }}
          />
        </div>

        {/* 右侧：站点详情浮窗 */}
        <div className="sitemap-side">
          <div className="panel">
            <div className="panel-title">
              <span><EnvironmentOutlined /> 站点详情</span>
              <Tag color={selected.state === 'online' ? 'green' : 'red'}>
                {STATE_TEXT(selected.state)}
              </Tag>
            </div>
            <div className="sitemap-info">
              <div className="sitemap-info-row"><span>站点名称</span><b>{selected.name}</b></div>
              <div className="sitemap-info-row"><span>站点类型</span><b>{selected.typeLabel}</b></div>
              <div className="sitemap-info-row"><span>装机功率</span><b>{selected.rated.power}</b></div>
              <div className="sitemap-info-row"><span>装机容量</span><b>{selected.rated.capacity}</b></div>
              <div className="sitemap-info-row"><span>位置</span><b>{selected.location}</b></div>
              <div className="sitemap-info-row"><span>经度 / 纬度</span><b>{selected.lng} / {selected.lat}</b></div>
              <div className="sitemap-info-row"><span>今日充电</span><b>{selected.ov.chargeToday} kWh</b></div>
              <div className="sitemap-info-row"><span>今日放电</span><b>{selected.ov.dischargeToday} kWh</b></div>
              <div className="sitemap-info-row"><span>昨日收益</span><b className="accent">¥ {selected.ov.incomeYesterday}</b></div>
            </div>
            <div className="sitemap-actions">
              <Button type="default" icon={<CompassOutlined />} onClick={() => { setKeyword(selected.name); setProvince('all'); setRegion('all'); }}>定位</Button>
              <Button type="default" icon={<ReloadOutlined />} onClick={resetAll}>恢复全部显示</Button>
              <Button type="primary" icon={<ArrowRightOutlined />} onClick={enterMonitor}>进入监控</Button>
            </div>
          </div>

          {/* 站点列表（支持隐藏站点） */}
          <div className="panel sitemap-list-panel">
            <div className="panel-title"><span>站点列表</span><small>点击定位详情 · 眼睛图标可隐藏站点</small></div>
            {markers.map((m) => (
              <div
                key={m.id}
                className={`sitemap-list-item${m.id === selectedId ? ' active' : ''}${m.hidden ? ' hidden' : ''}`}
                onClick={() => { setSelectedId(m.id); setKeyword(''); }}
              >
                <span className="dot" style={{ background: STATE_COLOR(m.state), boxShadow: `0 0 6px ${STATE_COLOR(m.state)}` }} />
                <span className="sitemap-list-name">{m.name}</span>
                <span className="sitemap-list-meta">{m.typeLabel}</span>
                {m.state !== 'online' ? <AlertOutlined style={{ color: STATE_COLOR(m.state) }} /> : null}
                <Tooltip title={m.hidden ? '取消隐藏' : '隐藏站点（不计入统计）'}>
                  <Button
                    type="text"
                    size="small"
                    className="sitemap-hide-btn"
                    icon={m.hidden ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                    onClick={(e) => { e.stopPropagation(); toggleHide(m.id); }}
                  />
                </Tooltip>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SiteMap;
